/**
 * Workspace Context
 *
 * Manages the workspace state including:
 * - Open artifacts (tabs)
 * - Active artifact
 * - CRUD operations for artifacts
 * - Auto-save to backend
 * - Load from backend on startup
 */

import React, {
  createContext,
  useContext,
  useReducer,
  useCallback,
  useMemo,
  useEffect,
  useRef,
} from 'react';
import type {
  Artifact,
  WorkspaceArtifactState,
  DocumentArtifact,
  EmailArtifact,
  ReportArtifact,
  ArtifactType,
  DocumentFormat,
} from './types';
import { createDocumentArtifact, createEmailArtifact } from './types';
import * as artifactsApi from '../../../api/services/artifacts';
import type { AgentCitation } from '../../../api/services/agent';

// Actions
type WorkspaceAction =
  | { type: 'SET_ARTIFACTS'; artifacts: Artifact[] }
  | { type: 'ADD_ARTIFACT'; artifact: Artifact }
  | { type: 'UPDATE_ARTIFACT'; id: string; updates: Partial<Artifact> }
  | { type: 'DELETE_ARTIFACT'; id: string }
  | { type: 'SET_ACTIVE_ARTIFACT'; id: string | null }
  | { type: 'OPEN_ARTIFACT'; id: string }
  | { type: 'CLOSE_ARTIFACT'; id: string }
  | { type: 'REORDER_TABS'; fromIndex: number; toIndex: number }
  | { type: 'SET_LOADING'; loading: boolean }
  | { type: 'SET_SYNCING'; artifactId: string; syncing: boolean };

// Extended state with loading/syncing
interface ExtendedWorkspaceState extends WorkspaceArtifactState {
  loading: boolean;
  syncingIds: Set<string>;
}

// Initial state
const initialState: ExtendedWorkspaceState = {
  artifacts: [],
  activeArtifactId: null,
  openArtifactIds: [],
  loading: true,
  syncingIds: new Set(),
};

// Reducer
function workspaceReducer(
  state: ExtendedWorkspaceState,
  action: WorkspaceAction,
): ExtendedWorkspaceState {
  switch (action.type) {
    case 'SET_ARTIFACTS':
      return {
        ...state,
        artifacts: action.artifacts,
        loading: false,
      };

    case 'SET_LOADING':
      return { ...state, loading: action.loading };

    case 'SET_SYNCING': {
      const newSyncingIds = new Set(state.syncingIds);
      if (action.syncing) {
        newSyncingIds.add(action.artifactId);
      } else {
        newSyncingIds.delete(action.artifactId);
      }
      return { ...state, syncingIds: newSyncingIds };
    }

    case 'ADD_ARTIFACT': {
      const newArtifacts = [...state.artifacts, action.artifact];
      const newOpenIds = [...state.openArtifactIds, action.artifact.id];
      return {
        ...state,
        artifacts: newArtifacts,
        openArtifactIds: newOpenIds,
        activeArtifactId: action.artifact.id,
      };
    }

    case 'UPDATE_ARTIFACT': {
      const index = state.artifacts.findIndex((a) => a.id === action.id);
      if (index === -1) return state;

      const updated = {
        ...state.artifacts[index],
        ...action.updates,
        updatedAt: new Date().toISOString(),
      } as Artifact;

      const newArtifacts = [...state.artifacts];
      newArtifacts[index] = updated;

      return { ...state, artifacts: newArtifacts };
    }

    case 'DELETE_ARTIFACT': {
      const newArtifacts = state.artifacts.filter((a) => a.id !== action.id);
      const newOpenIds = state.openArtifactIds.filter((id) => id !== action.id);
      let newActiveId = state.activeArtifactId;

      if (state.activeArtifactId === action.id) {
        const deletedIndex = state.openArtifactIds.indexOf(action.id);
        if (newOpenIds.length > 0) {
          newActiveId = newOpenIds[Math.min(deletedIndex, newOpenIds.length - 1)];
        } else {
          newActiveId = null;
        }
      }

      return {
        ...state,
        artifacts: newArtifacts,
        openArtifactIds: newOpenIds,
        activeArtifactId: newActiveId,
      };
    }

    case 'SET_ACTIVE_ARTIFACT':
      return { ...state, activeArtifactId: action.id };

    case 'OPEN_ARTIFACT': {
      if (state.openArtifactIds.includes(action.id)) {
        return { ...state, activeArtifactId: action.id };
      }
      return {
        ...state,
        openArtifactIds: [...state.openArtifactIds, action.id],
        activeArtifactId: action.id,
      };
    }

    case 'CLOSE_ARTIFACT': {
      const newOpenIds = state.openArtifactIds.filter((id) => id !== action.id);
      let newActiveId = state.activeArtifactId;

      if (state.activeArtifactId === action.id) {
        const closedIndex = state.openArtifactIds.indexOf(action.id);
        if (newOpenIds.length > 0) {
          newActiveId = newOpenIds[Math.min(closedIndex, newOpenIds.length - 1)];
        } else {
          newActiveId = null;
        }
      }

      return {
        ...state,
        openArtifactIds: newOpenIds,
        activeArtifactId: newActiveId,
      };
    }

    case 'REORDER_TABS': {
      const newOpenIds = [...state.openArtifactIds];
      const [removed] = newOpenIds.splice(action.fromIndex, 1);
      newOpenIds.splice(action.toIndex, 0, removed);
      return { ...state, openArtifactIds: newOpenIds };
    }

    default:
      return state;
  }
}

// Convert backend artifact to frontend format
function backendToFrontend(backend: artifactsApi.Artifact): Artifact {
  const base = {
    id: backend.id,
    title: backend.title,
    createdAt: backend.created_at,
    updatedAt: backend.updated_at,
    createdBy: 'user' as const,
    sourceMessageId: backend.source_message_id ?? undefined,
    citations: backend.citations,
    status: backend.status,
    access_level: backend.access_level,
  };

  const content = backend.content as Record<string, unknown>;
  const backendAny = backend as unknown as Record<string, unknown>;

  switch (backend.artifact_type) {
    case 'document': {
      const rawContent = content.content as unknown;
      const rawFormat = (content.format as string | undefined) || 'richtext';

      const format = (rawFormat as DocumentFormat) || 'richtext';

      return {
        ...base,
        type: 'document',
        content: rawContent ?? '',
        format,
        sourceOcrId: (backendAny as Record<string, unknown>).source_ocr_id as string | undefined,
        sourceFilename: (backendAny as Record<string, unknown>).source_filename as
          | string
          | undefined,
      } as DocumentArtifact;
    }

    case 'email':
      return {
        ...base,
        type: 'email',
        to: (content.to as string[]) || [],
        cc: content.cc as string[] | undefined,
        bcc: content.bcc as string[] | undefined,
        subject: (content.subject as string) || '',
        body: (content.body as string) || '',
        bodyFormat: (content.bodyFormat as 'html' | 'plain') || 'html',
        attachments: content.attachments as EmailArtifact['attachments'],
      } as EmailArtifact;

    case 'report':
      return {
        ...base,
        type: 'report',
        sections: (content.sections as ReportArtifact['sections']) || [],
      } as ReportArtifact;

    case 'data':
      return {
        ...base,
        type: 'data',
        columns: (content.columns as Artifact['type'] extends 'data' ? Artifact : never) || [],
        rows: (content.rows as Record<string, unknown>[]) || [],
        sourceFile: content.sourceFile as string | undefined,
      } as Artifact;

    default:
      throw new Error(`Unknown artifact type: ${backend.artifact_type}`);
  }
}

// Convert frontend artifact to backend format
function frontendToBackendContent(artifact: Artifact): Record<string, unknown> {
  if (artifact.type === 'document') {
    const doc = artifact as DocumentArtifact;
    if (doc.format === 'tiptap_json' || typeof doc.content === 'object') {
      return {
        content: doc.content,
        format: 'tiptap_json',
      };
    }
    return {
      content: doc.content,
      format: 'html',
    };
  }
  if (artifact.type === 'email') {
    return {
      to: artifact.to,
      cc: artifact.cc,
      bcc: artifact.bcc,
      subject: artifact.subject,
      body: artifact.body,
      bodyFormat: artifact.bodyFormat,
      attachments: artifact.attachments,
    };
  }
  if (artifact.type === 'report') {
    return {
      sections: artifact.sections,
    };
  }
  if (artifact.type === 'data') {
    return {
      columns: artifact.columns,
      rows: artifact.rows,
      sourceFile: artifact.sourceFile,
    };
  }
  return {};
}

// Context type
interface WorkspaceContextType {
  state: ExtendedWorkspaceState;
  // Artifact operations
  createDocument: (
    title: string,
    content?: string,
    createdBy?: 'user' | 'agent',
  ) => Promise<DocumentArtifact>;
  createEmail: (
    title: string,
    data?: Partial<
      Pick<
        EmailArtifact,
        | 'to'
        | 'cc'
        | 'bcc'
        | 'subject'
        | 'body'
        | 'bodyFormat'
        | 'attachments'
        | 'citations'
        | 'sourceMessageId'
      >
    >,
    createdBy?: 'user' | 'agent',
  ) => Promise<EmailArtifact>;
  updateArtifact: (id: string, updates: Partial<Artifact>) => void;
  saveArtifactImmediately: (id: string, updates: Partial<Artifact>) => Promise<void>;
  deleteArtifact: (id: string) => Promise<void>;
  // Tab operations
  openArtifact: (id: string) => void;
  closeArtifact: (id: string) => void;
  setActiveArtifact: (id: string | null) => void;
  // Helpers
  getArtifact: (id: string) => Artifact | undefined;
  activeArtifact: Artifact | undefined;
  openArtifacts: Artifact[];
  // Persistence helpers
  refreshArtifacts: () => Promise<void>;
  isSyncing: (id: string) => boolean;
  // Citation panel
  openCitation: AgentCitation | null;
  setOpenCitation: (citation: AgentCitation | null) => void;
  // History / revert
  revertArtifact: (id: string) => Promise<void>;
}

const WorkspaceContext = createContext<WorkspaceContextType | null>(null);

// Debounce helper
function useDebouncedCallback<TArgs extends unknown[]>(
  callback: (...args: TArgs) => void,
  delay: number,
): (...args: TArgs) => void {
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const callbackRef = useRef(callback);
  callbackRef.current = callback;

  return useCallback(
    (...args: TArgs) => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      timeoutRef.current = setTimeout(() => {
        callbackRef.current(...args);
      }, delay);
    },
    [delay],
  );
}

export const WorkspaceProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [state, dispatch] = useReducer(workspaceReducer, initialState);
  const pendingSavesRef = useRef<Map<string, Partial<Artifact>>>(new Map());
  const [openCitation, setOpenCitation] = React.useState<AgentCitation | null>(null);

  // Load artifacts from backend on mount
  useEffect(() => {
    const loadArtifacts = async () => {
      try {
        const backendArtifacts = await artifactsApi.listArtifacts();
        const frontendArtifacts: Artifact[] = [];
        for (let i = 0; i < backendArtifacts.length; i++) {
          try {
            frontendArtifacts.push(backendToFrontend(backendArtifacts[i]));
          } catch (mapErr) {
            console.warn('backendToFrontend failed for artifact', backendArtifacts[i]?.id ?? i, mapErr);
          }
        }
        dispatch({ type: 'SET_ARTIFACTS', artifacts: frontendArtifacts });
      } catch (error) {
        console.error('Failed to load artifacts:', error);
        dispatch({ type: 'SET_LOADING', loading: false });
      }
    };

    loadArtifacts();
  }, []);

  // Debounced save to backend
  const saveToBackend = useDebouncedCallback(async (id: string, updates: Partial<Artifact>) => {
    const artifact = state.artifacts.find((a) => a.id === id);
    if (!artifact) return;

    dispatch({ type: 'SET_SYNCING', artifactId: id, syncing: true });

    try {
      // Merge updates while preserving the artifact type
      const merged = { ...artifact, ...updates } as Artifact;
      const updatedArtifact = await artifactsApi.updateArtifact(id, {
        title: merged.title,
        content: frontendToBackendContent(merged),
        status: merged.status,
        citations: merged.citations?.map((c) => ({
          path: c.path,
          start_line: c.start_line ?? (c as unknown as { startLine: number }).startLine ?? 0,
          end_line: c.end_line ?? (c as unknown as { endLine: number }).endLine ?? 0,
          text: c.text ?? '',
        })),
      });

      // Update local state with decoded artifact from backend
      const frontendArtifact = backendToFrontend(updatedArtifact);
      dispatch({ type: 'UPDATE_ARTIFACT', id, updates: frontendArtifact });
    } catch (error) {
      console.error('Failed to save artifact:', error);
    } finally {
      dispatch({ type: 'SET_SYNCING', artifactId: id, syncing: false });
      pendingSavesRef.current.delete(id);
    }
  }, 1000);

  const refreshArtifacts = useCallback(async () => {
    dispatch({ type: 'SET_LOADING', loading: true });
    try {
      const backendArtifacts = await artifactsApi.listArtifacts();
      const frontendArtifacts = backendArtifacts.map(backendToFrontend);
      dispatch({ type: 'SET_ARTIFACTS', artifacts: frontendArtifacts });
    } catch (error) {
      console.error('Failed to refresh artifacts:', error);
      dispatch({ type: 'SET_LOADING', loading: false });
    }
  }, []);

  const createDocument = useCallback(
    async (title: string, content = '', createdBy: 'user' | 'agent' = 'user') => {
      // If no initial content is provided, start as an empty TipTap JSON document
      const isEmptyContent = !content || content.trim().length === 0;

      const emptyTiptapDoc = { type: 'doc', content: [] as unknown[] };

      // Create locally first for instant feedback
      const localArtifact = isEmptyContent
        ? createDocumentArtifact(title, createdBy, {
            content: emptyTiptapDoc,
            format: 'tiptap_json',
          })
        : createDocumentArtifact(title, createdBy, { content, format: 'markdown' });

      dispatch({ type: 'ADD_ARTIFACT', artifact: localArtifact });

      // Then persist to backend
      try {
        const backendArtifact = await artifactsApi.createArtifact({
          artifact_type: 'document',
          title,
          content: isEmptyContent
            ? { content: emptyTiptapDoc, format: 'tiptap_json' }
            : { content, format: 'html' },
          status: 'draft',
        });

        // Update with backend ID (replace local artifact)
        const frontendArtifact = backendToFrontend(backendArtifact);
        dispatch({ type: 'DELETE_ARTIFACT', id: localArtifact.id });
        dispatch({ type: 'ADD_ARTIFACT', artifact: frontendArtifact });

        return frontendArtifact as DocumentArtifact;
      } catch (error) {
        console.error('Failed to create document:', error);
        return localArtifact;
      }
    },
    [],
  );

  const createEmail = useCallback(
    async (
      title: string,
      data?: Partial<
        Pick<
          EmailArtifact,
          | 'to'
          | 'cc'
          | 'bcc'
          | 'subject'
          | 'body'
          | 'bodyFormat'
          | 'attachments'
          | 'citations'
          | 'sourceMessageId'
        >
      >,
      createdBy: 'user' | 'agent' = 'user',
    ) => {
      const localArtifact = createEmailArtifact(title, createdBy, data);
      dispatch({ type: 'ADD_ARTIFACT', artifact: localArtifact });

      try {
        const backendArtifact = await artifactsApi.createArtifact({
          artifact_type: 'email',
          title,
          content: {
            to: data?.to || [],
            cc: data?.cc,
            bcc: data?.bcc,
            subject: data?.subject || '',
            body: data?.body || '',
            bodyFormat: data?.bodyFormat || 'html',
            attachments: data?.attachments,
          },
          status: 'draft',
          citations: data?.citations?.map((c) => ({
            path: c.path,
            start_line: c.start_line ?? (c as unknown as { startLine: number }).startLine ?? 0,
            end_line: c.end_line ?? (c as unknown as { endLine: number }).endLine ?? 0,
            text: c.text ?? '',
          })),
        });

        const frontendArtifact = backendToFrontend(backendArtifact);
        dispatch({ type: 'DELETE_ARTIFACT', id: localArtifact.id });
        dispatch({ type: 'ADD_ARTIFACT', artifact: frontendArtifact });

        return frontendArtifact as EmailArtifact;
      } catch (error) {
        console.error('Failed to create email:', error);
        return localArtifact;
      }
    },
    [],
  );

  /**
   * ACCESS CONTROL RULES (Not yet implemented, but documented for future implementation):
   * =====================================================================================
   *
   * Edit Permissions:
   * - Users can ONLY edit artifacts they own (access_level === 'owner' or undefined)
   * - Users can ONLY edit artifacts shared with them with "edit" permission (access_level === 'edit')
   * - Users CANNOT edit artifacts shared with "view" permission only (access_level === 'view')
   *
   * Future Implementation:
   * - Check artifact.access_level before calling updateArtifact()
   * - Return early or show error if user attempts to edit with access_level === 'view'
   * - Frontend should disable editing UI for view-only artifacts
   */
  const updateArtifact = useCallback(
    (id: string, updates: Partial<Artifact>) => {
      // TODO: Check access_level before allowing updates
      // const artifact = state.artifacts.find((a) => a.id === id);
      // if (artifact?.access_level === 'view') {
      //   console.warn('Cannot edit artifact with view-only access');
      //   return;
      // }

      // Update locally immediately
      dispatch({ type: 'UPDATE_ARTIFACT', id, updates });

      // Queue debounced save
      const pending = pendingSavesRef.current.get(id) || {};
      pendingSavesRef.current.set(id, { ...pending, ...updates });
      saveToBackend(id, { ...pending, ...updates });
    },
    [saveToBackend, state.artifacts],
  );

  const saveArtifactImmediately = useCallback(
    async (id: string, updates: Partial<Artifact>) => {
      const artifact = state.artifacts.find((a) => a.id === id);
      if (!artifact) return;

      // Cancel any pending debounced saves for this artifact
      pendingSavesRef.current.delete(id);

      dispatch({ type: 'SET_SYNCING', artifactId: id, syncing: true });

      try {
        // Merge updates while preserving the artifact type
        const merged = { ...artifact, ...updates } as Artifact;
        const updatedArtifact = await artifactsApi.updateArtifact(id, {
          title: merged.title,
          content: frontendToBackendContent(merged),
          status: merged.status,
          citations: merged.citations?.map((c) => ({
            path: c.path,
            start_line: c.start_line ?? (c as unknown as { startLine: number }).startLine ?? 0,
            end_line: c.end_line ?? (c as unknown as { endLine: number }).endLine ?? 0,
            text: c.text ?? '',
          })),
        });

        // Update local state with decoded artifact from backend
        const frontendArtifact = backendToFrontend(updatedArtifact);
        dispatch({ type: 'UPDATE_ARTIFACT', id, updates: frontendArtifact });
      } catch (error) {
        console.error('Failed to save artifact:', error);
      } finally {
        dispatch({ type: 'SET_SYNCING', artifactId: id, syncing: false });
      }
    },
    [state.artifacts],
  );

  const revertArtifact = useCallback(async (id: string) => {
    dispatch({ type: 'SET_SYNCING', artifactId: id, syncing: true });
    try {
      const versions = await artifactsApi.listArtifactVersions(id);
      const target = versions[0];
      if (!target) {
        dispatch({ type: 'SET_SYNCING', artifactId: id, syncing: false });
        return;
      }
      const updated = await artifactsApi.revertArtifactVersion(id, target.id);
      const frontend = backendToFrontend(updated);
      dispatch({ type: 'UPDATE_ARTIFACT', id, updates: frontend });
    } catch (error) {
      console.error('Failed to revert artifact:', error);
    } finally {
      dispatch({ type: 'SET_SYNCING', artifactId: id, syncing: false });
    }
  }, []);

  const deleteArtifact = useCallback(async (id: string) => {
    // Delete locally first
    dispatch({ type: 'DELETE_ARTIFACT', id });

    // Then from backend
    try {
      await artifactsApi.deleteArtifact(id);
    } catch (error) {
      console.error('Failed to delete artifact:', error);
      // Could restore locally here if needed
    }
  }, []);

  const openArtifact = useCallback((id: string) => {
    dispatch({ type: 'OPEN_ARTIFACT', id });
  }, []);

  const closeArtifact = useCallback((id: string) => {
    dispatch({ type: 'CLOSE_ARTIFACT', id });
  }, []);

  const setActiveArtifact = useCallback((id: string | null) => {
    dispatch({ type: 'SET_ACTIVE_ARTIFACT', id });
  }, []);

  const getArtifact = useCallback(
    (id: string) => {
      return state.artifacts.find((a) => a.id === id);
    },
    [state.artifacts],
  );

  const activeArtifact = useMemo(() => {
    return state.activeArtifactId
      ? state.artifacts.find((a) => a.id === state.activeArtifactId)
      : undefined;
  }, [state.activeArtifactId, state.artifacts]);

  const openArtifacts = useMemo(() => {
    return state.openArtifactIds
      .map((id) => state.artifacts.find((a) => a.id === id))
      .filter((a): a is Artifact => a !== undefined);
  }, [state.openArtifactIds, state.artifacts]);

  const isSyncing = useCallback(
    (id: string) => {
      return state.syncingIds.has(id);
    },
    [state.syncingIds],
  );

  const value = useMemo<WorkspaceContextType>(
    () => ({
      state,
      createDocument,
      createEmail,
      updateArtifact,
      saveArtifactImmediately,
      deleteArtifact,
      openArtifact,
      closeArtifact,
      setActiveArtifact,
      getArtifact,
      activeArtifact,
      openArtifacts,
      refreshArtifacts,
      isSyncing,
      openCitation,
      setOpenCitation,
      revertArtifact,
    }),
    [
      state,
      createDocument,
      createEmail,
      updateArtifact,
      saveArtifactImmediately,
      deleteArtifact,
      openArtifact,
      closeArtifact,
      setActiveArtifact,
      getArtifact,
      activeArtifact,
      openArtifacts,
      refreshArtifacts,
      openCitation,
      isSyncing,
      revertArtifact,
    ],
  );

  return <WorkspaceContext.Provider value={value}>{children}</WorkspaceContext.Provider>;
};

export const useWorkspace = () => {
  const context = useContext(WorkspaceContext);
  if (!context) {
    throw new Error('useWorkspace must be used within a WorkspaceProvider');
  }
  return context;
};

export default WorkspaceContext;
